import CustomTooltip from '../feature/shared/CustomTooltip';
import {
    Assignment,
    ExpandLess,
    ExpandMore,
    Notifications,
    People,
} from '@mui/icons-material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BarChartIcon from '@mui/icons-material/BarChart';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EngineeringIcon from '@mui/icons-material/Engineering';
import HelpIcon from '@mui/icons-material/Help';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MailIcon from '@mui/icons-material/Mail';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupIcon from '@mui/icons-material/Group';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import {
    AppBar,
    Toolbar,
    IconButton,
    Menu,
    MenuItem,
    Drawer,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Collapse,
    Divider,
    ListItemButton,
    useMediaQuery,
    Badge,
    Stack,
    ListItemAvatar,
    useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    useGetBillingReportQuery,
    useGetCompanyGuestsInfoQuery,
    useGetIndividualOwnerQuery,
    useGetProfileCompanyInfoQuery,
    useGetProfileDetailsQuery,
    useGetReportQuery,
    useGetRsvpRequestsQuery,
} from '../app/Slices/SevenDayApiSlice';
import {
    ActionType,
    selectSnackBars,
    Toast,
} from '../app/Slices/SnackBarSlice';
import NotificationsCenter from '../feature/components/Notifications Center/NotificationsCenter';
import ProfilePicture from '../feature/components/Profile/ProfilePicture';
import {
    clearProfileSetupBannerDismissed,
    dismissProfileSetupBanner,
    getMandatoryProfileSetupMissingItems,
    isProfileSetupBannerDismissed,
    PROFILE_SETUP_HIGHLIGHT_STATE_KEY,
} from '../feature/components/Profile/profileSetupBanner';
import toast from '../feature/shared/Toast';
import UserRole from '../shared/AuthRoles';
import moment from 'moment';
import { StreamChat } from 'stream-chat';
import { selectVisitTypeSliceData } from '../app/Slices/VisitTypeSlice';
import {
    ContractorRole,
    External_Attendee_Role,
    OwnerRole,
} from '../feature/enums/users';
import { differenceInCalendarDays } from 'date-fns';
import { resetReservationFilters } from '../app/Slices/ReservationFiltersSlice';
import { openCalendarViewReservationAction } from '../app/Slices/CalendarViewSlice';
import {
    resetExperienceFilters,
    selectExperienceFilters,
} from '../app/Slices/ExperienceFiltersSlice';
import { resetExperienceSearchFilter } from '../app/Slices/ExperienceSearchSlice';
import AIAssistant from './AIAssistant';

const DRAWER_WIDTH = 350;
const COLLAPSED_WIDTH = 72;
const DRAWER_HEADER_HEIGHT = 64;

type MenuItemType = {
    title: string;
    icon?: JSX.Element;
    path: string;
    subItems?: { title: string; path: string }[];
    badgeCount?: number;
};

const MenuLabelWithCount = ({
    label,
    count,
}: {
    label: string;
    count?: number;
}) => {
    if (!count) {
        return <>{label}</>;
    }

    return (
        <Badge
            overlap='circular'
            badgeContent={count}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
                display: 'inline-flex',
                '& .MuiBadge-badge': {
                    color: 'custom.sidebar.badgeColor',
                    backgroundColor: 'custom.sidebar.badgeBackgroundColor',

                    top: 2,
                    right: 2,
                },
            }}
        >
            <Box component='span' sx={{ display: 'inline-block', pr: 1.25 }}>
                {label}
            </Box>
        </Badge>
    );
};

const renderMenuItemIcon = (item: MenuItemType, showBadgeOnIcon: boolean) => {
    if (!showBadgeOnIcon || !item.badgeCount || !item.icon) {
        return item.icon;
    }

    return (
        <Badge
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            badgeContent={item.badgeCount}
            sx={{
                '& .MuiBadge-badge': {
                    color: 'custom.sidebar.badgeColor',
                    backgroundColor: 'custom.sidebar.badgeBackgroundColor',
                },
            }}
        >
            {item.icon}
        </Badge>
    );
};

const LogoLink = ({
    to,
    src,
    height,
    centered,
}: {
    to: string;
    src: string;
    height: number;
    centered?: boolean;
}) => {
    return (
        <Link
            to={to}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: centered ? 'center' : 'flex-start',
                width: centered ? '100%' : 'auto',
            }}
        >
            <img
                alt='primarylogo'
                src={src}
                style={{
                    padding: 0,
                    width: 'auto',
                    height,
                }}
            />
        </Link>
    );
};

const MiniDrawerHeader = ({
    logoSrc,
    isMini,
    onToggleMini,
    theme,
    logoTo,
}: {
    logoSrc: string;
    isMini: boolean;
    onToggleMini: () => void;
    theme: any;
    logoTo: string;
}) => {
    if (isMini) {
        return (
            <Stack
                sx={{
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                spacing={1}
            >
                <CustomTooltip title='Expand menu' placement='right' arrow>
                    <IconButton
                        disableRipple
                        onClick={onToggleMini}
                        sx={{
                            mt: 0.5,
                            '&:hover': {
                                backgroundColor: 'transparent',
                                opacity: 0.7,
                            },
                        }}
                    >
                        <KeyboardArrowRightIcon
                            sx={{
                                fontSize: 20,
                                color: theme.palette.custom.lightTextColor,
                            }}
                        />
                    </IconButton>
                </CustomTooltip>

                <Divider sx={{ width: '100%', opacity: 0.2 }} />

                <LogoLink to={logoTo} src={logoSrc} height={42} centered />
            </Stack>
        );
    }

    return (
        <Stack
            direction='row'
            sx={{
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <LogoLink to={logoTo} src={logoSrc} height={42} />
            <CustomTooltip title='Collapse menu' placement='right' arrow>
                <IconButton
                    disableRipple
                    onClick={onToggleMini}
                    sx={{
                        '&:hover': {
                            backgroundColor: 'transparent',
                            opacity: 0.7,
                        },
                    }}
                >
                    <KeyboardArrowLeftIcon
                        sx={{
                            fontSize: 20,
                            color: theme.palette.custom.lightTextColor,
                        }}
                    />
                </IconButton>
            </CustomTooltip>
        </Stack>
    );
};

const DrawerHeader = ({
    isMobile,
    isIpad,
    canMini,
    isMini,
    logoSrc,
    onToggleMini,
    theme,
    logoTo,
}: {
    isMobile: boolean;
    isIpad: boolean;
    canMini: boolean;
    isMini: boolean;
    logoSrc: string;
    onToggleMini: () => void;
    theme: any;
    logoTo: string;
}) => {
    return (
        <Toolbar
            sx={{
                minHeight: DRAWER_HEADER_HEIGHT,
                px: canMini && isMini ? 0 : 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: isMobile || isIpad ? 'flex-start' : 'center',
            }}
        >
            {canMini && !isMobile && !isIpad ? (
                <MiniDrawerHeader
                    logoSrc={logoSrc}
                    isMini={isMini}
                    onToggleMini={onToggleMini}
                    theme={theme}
                    logoTo={logoTo}
                />
            ) : (
                <LogoLink
                    to={logoTo}
                    src={logoSrc}
                    height={!isMobile ? 80 : 50}
                />
            )}
        </Toolbar>
    );
};

const MobileTopBar = ({
    logoSrc,
    onMenu,
    to,
}: {
    logoSrc: string;
    onMenu: () => void;
    to: string;
}) => {
    return (
        <AppBar
            position='fixed'
            elevation={2}
            sx={{
                width: '100%',
                overflow: 'hidden',
                backgroundColor: 'custom.toolbar',
                top: 'var(--app-prompt-banner-height, 0px)',
            }}
        >
            <Toolbar
                disableGutters
                sx={{
                    backgroundColor: 'custom.toolbar',
                    color: 'custom.lightTextColor',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    width: '100%',
                }}
            >
                <LogoLink to={to} src={logoSrc} height={42} />
                <IconButton
                    onClick={onMenu}
                    sx={{ color: 'custom.lightTextColor' }}
                >
                    <MenuIcon />
                </IconButton>
            </Toolbar>
        </AppBar>
    );
};

const DrawerCloseButton = ({ onClick }: { onClick: () => void }) => {
    return (
        <IconButton
            onClick={onClick}
            sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 10,
                color: 'custom.sidebar.closeIconColor',
                backgroundColor: 'custom.sidebar.closeIconBackgroundColor',
                '&:hover': {
                    color: 'custom.sidebar.closeIconHoverColor',
                    backgroundColor:
                        'custom.sidebar.closeIconHoverBackgroundColor',
                },
            }}
        >
            <CloseIcon />
        </IconButton>
    );
};

const CustomAppBar = () => {
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const toasts = useSelector(selectSnackBars);
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(true);
    const [openMenus, setOpenMenus] = useState<any>({});
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);

    const [drawerProfileAnchor, setDrawerProfileAnchor] =
        useState<HTMLElement | null>(null);
    const drawerProfileOpen = Boolean(drawerProfileAnchor);

    const openDrawerProfileMenu = (el: HTMLElement) =>
        setDrawerProfileAnchor(el);
    const closeDrawerProfileMenu = () => setDrawerProfileAnchor(null);

    const theme = useTheme();
    const navigate = useNavigate();

    const roleStr = String((loggedInUser as any)?.role ?? '').toLowerCase();
    const isGuestAdmin = roleStr.includes('guest_admin');

    const DASHBOARD_PATH = '/dashboard';

    const logoTo = isGuestAdmin
        ? DASHBOARD_PATH
        : roleStr === 'owner_company' || roleStr === 'guest_company'
          ? '/users'
          : roleStr === 'external_attendee_user'
            ? '/reservations'
            : DASHBOARD_PATH;

    const { data: getIndividualCompany } = useGetIndividualOwnerQuery(
        `owners/${loggedInUser?.company}`,
    );

    const visitTypeSelected = useSelector(selectVisitTypeSliceData);

    const API_KEY = process.env.REACT_APP_STREAM_API_KEY as string;
    const client = StreamChat.getInstance(API_KEY);

    const { data: profileDetails } = useGetProfileDetailsQuery('/profile');
    useGetProfileCompanyInfoQuery(`/guests/${loggedInUser?.company}`, {
        skip: roleStr !== 'guest_admin',
    });

    const [unreadCount, setUnreadCount] = useState<number>(0);

    useEffect(() => {
        const handler = (event: any) => {
            if (event.unread_channels !== undefined) {
                setUnreadCount(event.unread_channels);
            }
            if (event.me) {
                setUnreadCount(client.user?.unread_channels as number);
            }
        };

        client?.on(handler);

        return () => {
            try {
                (client as any)?.off?.(handler);
            } catch {}
        };
    }, [client]);

    const { data: getPendingReservations } = useGetReportQuery(
        `/reports/bookings?from=${moment('01/01/2023').format(
            'YYYY-MM-DD',
        )}&to=${moment('01/01/3000').format('YYYY-MM-DD')}&status=pending`,
    );

    const { data: rsvpRequests } = useGetRsvpRequestsQuery(
        {},
        {
            skip: !loggedInUser || roleStr.includes('contractor'),
        },
    );

    useSelector(selectExperienceFilters);
    const dispatch = useDispatch();

    const clearAllToasts = () => {
        try {
            (toasts || []).forEach((t: Toast) => toast.remove(t.id));
        } catch {}
    };

    useEffect(() => {
        if (!location.pathname.includes('/reservations')) {
            dispatch(resetReservationFilters());
            dispatch(openCalendarViewReservationAction({ view: false }));
        }

        if (!location.pathname.includes('/viewExperiences')) {
            dispatch(resetExperienceFilters());
        }

        if (
            !location.pathname.includes('/reservations') &&
            !location.pathname.includes('/viewExperiences')
        ) {
            sessionStorage.removeItem('searchValue');
        }
    }, [location.pathname, dispatch]);

    const avatarStyles = {
        height: '30px',
        width: '30px',
        fontSize: '12px',
        border: '1px solid',
        color: 'custom.sidebar.iconTextColor',
        '&::after': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: '100%',
            transition: 'opacity 0.1s ease-in-out',
            opacity: 0,
        },
        '&:hover::after': { opacity: 1 },
        '&:hover': { transition: '0.1s ease-in-out', opacity: 0.8 },
    };

    const commonReports = [
        { title: 'Reservations Report', path: '/reports/reservationsReport' },
        { title: 'Monthly Report', path: '/reports/monthlyReport' },
        { title: 'Quarterly Report', path: '/reports/quarterlyReport' },
        { title: 'YTD Report', path: '/reports/ytdReport' },
        { title: 'FY Report', path: '/reports/fyReport' },
    ];

    const ownerReports = [
        ...commonReports,
        { title: 'Cancellation Report', path: '/reports/cancellationReport' },
        { title: 'Guest Details', path: '/reports/guestDetails' },
        { title: 'Top Guests', path: '/reports/topGuests' },
        { title: 'Top Experiences', path: '/reports/topExperiences' },
        { title: 'Occupancy Report', path: '/reports/occupancy ' },
    ];

    const adminsReports = [
        ...commonReports,
        { title: 'Cancellation Report', path: '/reports/cancellationReport' },
        { title: 'Declined Report', path: '/reports/declinedReport' },
        { title: 'Host Payments', path: '/reports/hostPayments' },
        { title: 'Guest Invoices', path: '/reports/guestInvoices' },
        { title: 'Mailing Lists', path: '/reports/mailingLists' },
        { title: 'Top Guests', path: '/reports/topGuests' },
        { title: 'Top Experiences', path: '/reports/topExperiences' },
    ];

    const guestAdminReports = [
        ...commonReports,
        { title: 'Cancellation Report', path: '/reports/cancellationReport' },
        { title: 'Top Bookers', path: '/reports/topBookers' },
        { title: 'Booker Details', path: '/reports/bookerDetails' },
    ];

    const guestUserReports = [...commonReports];

    const reports = (() => {
        switch (true) {
            case roleStr.includes('owner'):
                return ownerReports;
            case roleStr.includes('guest'):
                if (roleStr.includes('admin')) return guestAdminReports;
                return guestUserReports;
            default:
                return adminsReports;
        }
    })();

    const getExperiencePath = () =>
        roleStr.includes('owner')
            ? '/viewExperiences/all'
            : `/viewExperiences/${visitTypeSelected?.view}`;

    const getAdminCentrePath = () =>
        roleStr.includes('owner') ? 'host' : 'guest';

    const getReservationsBadge = () =>
        (roleStr.includes('owner') || roleStr === 'admin') && (
            <Badge
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                badgeContent={getPendingReservations?.length}
                sx={{
                    '& .MuiBadge-badge': {
                        color: 'custom.sidebar.badgeColor',
                        backgroundColor: 'custom.sidebar.badgeBackgroundColor',
                    },
                }}
            >
                <CalendarMonthIcon />
            </Badge>
        );

    const commonMenuItems = useMemo(() => {
        return {
            dashboard: {
                title: 'Dashboard',
                icon: <DashboardIcon />,
                path: '/dashboard',
            },
            experiences: {
                title: 'Experiences',
                icon: <LocationOnIcon />,
                path: getExperiencePath(),
            },
            reservations: {
                title: 'Reservations',
                icon: getReservationsBadge() || <CalendarMonthIcon />,
                path: '/reservations',
            },
            contractorList: {
                title: 'Jobs',
                icon: <EngineeringIcon />,
                path: '#',
                subItems: [
                    { title: 'Job List', path: `/jobList` },
                    { title: 'Recurring Jobs', path: `/recurringJobs` },
                ],
            },
            reports: {
                title: 'Reports',
                icon: <BarChartIcon />,
                path: '/reports',
            },
            admin: {
                title: 'Admin Centre',
                icon: <AdminPanelSettingsIcon />,
                path: `/adminCentre/${getAdminCentrePath()}`,
            },
            users: { title: 'Users', icon: <GroupIcon />, path: `/users` },
            settings: {
                title: 'Settings',
                icon: <SettingsIcon />,
                path: '#',
                subItems: [
                    {
                        title: 'Notification Settings',
                        path: '/notificationSettings',
                    },
                ],
            },
            help: {
                title: 'Help',
                icon: <HelpIcon />,
                path: '#',
                subItems:
                    roleStr === 'external_attendee_user'
                        ? [
                              { title: 'Privacy Policy', path: '/privacy' },
                              { title: 'Terms of Service', path: '/terms' },
                              { title: 'Report a problem', path: '/support' },
                          ]
                        : [
                              {
                                  title: 'Help Documentation',
                                  path: 'https://help.7-day.com/hc/en-us',
                              },
                              { title: 'Privacy Policy', path: '/privacy' },
                              { title: 'Terms of Service', path: '/terms' },
                              { title: 'Report a problem', path: '/support' },
                          ],
            },
        };
    }, [roleStr, visitTypeSelected?.view, getPendingReservations?.length]);

    const ownerMenuItems = roleStr.includes('admin')
        ? [
              commonMenuItems.dashboard,
              {
                  title: 'Experiences',
                  subItems: [
                      {
                          title: 'Manage Your Listings',
                          path: `/viewExperiences/all/${getIndividualCompany?.name
                              ?.replace(/ /g, '')
                              .toLowerCase()}`,
                      },
                      { title: 'View All Listings', path: getExperiencePath() },
                  ],
                  icon: <LocationOnIcon />,
              },
              {
                  title: 'Reservations',
                  subItems: [
                      { title: 'List View', path: '/reservations?view=list' },
                      {
                          title: 'Calendar View',
                          path: '/reservations?view=calendar',
                      },
                      { title: 'Daily Visitors', path: '/reservations/people' },
                  ],
                  icon: getReservationsBadge() || <CalendarMonthIcon />,
              },
              commonMenuItems.contractorList,
              { ...commonMenuItems.reports, subItems: reports },
              commonMenuItems.admin,
              {
                  ...commonMenuItems.settings,
                  subItems: [
                      {
                          title: 'Notification Settings',
                          icon: <Notifications />,
                          path: '/notificationSettings',
                      },
                  ],
              },
              commonMenuItems.help,
          ]
        : [
              commonMenuItems.dashboard,
              {
                  title: 'Experiences',
                  subItems: [
                      { title: 'View All Listings', path: getExperiencePath() },
                  ],
                  icon: <LocationOnIcon />,
              },
              commonMenuItems.reservations,
              { ...commonMenuItems.reports, subItems: reports },
              {
                  ...commonMenuItems.settings,
                  subItems: [
                      {
                          title: 'Notification Settings',
                          icon: <Notifications />,
                          path: '/notificationSettings',
                      },
                  ],
              },
              commonMenuItems.help,
          ];

    const adminMenuItems = [
        commonMenuItems.dashboard,
        commonMenuItems.experiences,
        commonMenuItems.reservations,
        { ...commonMenuItems.reports, subItems: reports },
        {
            ...commonMenuItems.settings,
            subItems: [{ title: 'Admin Centre', path: '/adminCentre/Host' }],
        },
        commonMenuItems.help,
    ];

    const externalAttendeeMenuItems: MenuItemType[] = [
        commonMenuItems.experiences,
        commonMenuItems.reservations,
    ];

    const guestMenuItems: MenuItemType[] = [
        commonMenuItems.experiences,
        commonMenuItems.reservations,
        { ...commonMenuItems.reports, subItems: reports },
    ];

    const isAdmin = roleStr.includes('admin');
    const isDepartmentLeader = roleStr.includes('department');

    if (isMobile || isIpad) {
        if (isAdmin) {
            if (isDepartmentLeader) {
                guestMenuItems.push(
                    commonMenuItems.users,
                    commonMenuItems.settings,
                    { ...commonMenuItems.help },
                );
            } else {
                guestMenuItems.unshift(commonMenuItems.dashboard);
                guestMenuItems.push(
                    commonMenuItems.admin,
                    commonMenuItems.settings,
                    { ...commonMenuItems.help },
                );
            }
        } else {
            guestMenuItems.push(commonMenuItems.settings, {
                ...commonMenuItems.help,
            });
        }
    } else if (isAdmin) {
        guestMenuItems.push(
            isDepartmentLeader ? commonMenuItems.users : commonMenuItems.admin,
        );
    }

    const contractorMenuItems = [
        { title: 'Jobs', icon: <Assignment />, path: '/jobs' },
        {
            title: 'Calendar',
            icon: <CalendarMonthIcon />,
            path: '/contractorCalendar',
        },
    ];

    if (roleStr === ContractorRole.Admin) {
        contractorMenuItems.push(
            { title: 'Clients', icon: <People />, path: '/contractorClients' },
            { title: 'Users', icon: <People />, path: '/users' },
        );
    }

    const getRsvpRequestsMenuItem = (): MenuItemType | null => {
        if (!rsvpRequests?.length) return null;
        return {
            title: 'RSVP Requests',
            icon: <HowToRegIcon />,
            path: '/rsvpRequests',
            badgeCount: rsvpRequests.length,
        };
    };

    const insertRsvpRequestsMenuItem = (
        items: MenuItemType[],
    ): MenuItemType[] => {
        const rsvpItem = getRsvpRequestsMenuItem();
        if (!rsvpItem) return items;
        const reservationsIndex = items.findIndex(
            (item) => item.title === 'Reservations',
        );
        if (reservationsIndex === -1) return [...items, rsvpItem];
        return [
            ...items.slice(0, reservationsIndex + 1),
            rsvpItem,
            ...items.slice(reservationsIndex + 1),
        ];
    };

    const mainMenuItems: MenuItemType[] = insertRsvpRequestsMenuItem(
        (roleStr.includes('owner')
            ? ownerMenuItems
            : roleStr === 'admin'
              ? adminMenuItems
              : roleStr.includes('contractor')
                ? contractorMenuItems
                : roleStr.includes('external_attendee')
                  ? externalAttendeeMenuItems
                  : guestMenuItems) as MenuItemType[],
    );

    const getProfileIcon = (role: string | string[], profile: any) => {
        if (
            String(role ?? '').includes('company') ||
            String(role ?? '').includes('contractor')
        ) {
            return (
                <AccountCircleIcon
                    sx={{
                        p: 0,
                        '&:hover': {
                            cursor: 'pointer',
                            color: 'primary.main',
                            backgroundColor: 'transparent',
                            background: 'none',
                        },
                    }}
                />
            );
        }

        return (
            <ProfilePicture
                profileDetails={profile}
                src={profile?.profilePicture?.src}
                sx={{ ...avatarStyles }}
            />
        );
    };

    const createMessageIcon = (count: any) => (
        <Badge
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            badgeContent={count}
            sx={{
                '& .MuiBadge-badge': {
                    color: 'custom.sidebar.badgeColor',
                    backgroundColor: 'custom.sidebar.badgeBackgroundColor',
                },
            }}
        >
            <MailIcon />
        </Badge>
    );

    const createProfileSubItems = () => [
        ...(loggedInUser?.role.includes('contractor')
            ? [
                  {
                      title: 'View Organisation',
                      path: '/contractorProfile',
                  },
              ]
            : []),
        ...(loggedInUser?.role.includes('owner')
            ? [
                  {
                      title: 'View Host Profile',
                      path: `/hosts/${loggedInUser?.company}`,
                  },
              ]
            : []),
        { title: 'View Profile', path: '/profile' },
        { title: 'Logout', path: '/login' },
    ];

    const secondaryOwnerMenuItems = [
        {
            title: 'Messages',
            icon: createMessageIcon(unreadCount),
            path: '/messages',
        },
        {
            title: 'Profile',
            icon: getProfileIcon(profileDetails?.role, profileDetails),
            path: '/profile',
            subItems: createProfileSubItems(),
        },
    ];

    const secondaryAdminMenuItems = [
        {
            title: 'Profile',
            icon: getProfileIcon(profileDetails?.role, profileDetails),
            path: '/profile',
            subItems: createProfileSubItems(),
        },
    ];

    const secondaryGuestMenuItems = (
        m: boolean,
        i: boolean,
        user: { role: string | string[] },
    ) => {
        const baseProfile = {
            title: 'Profile',
            icon: getProfileIcon(profileDetails?.role, profileDetails),
            subItems: createProfileSubItems(),
            path: '/profile',
        };

        const notificationsSettings = {
            title: 'Settings',
            icon: <SettingsIcon />,
            subItems: [
                {
                    title: 'Notification Settings',
                    path: '/notificationSettings',
                },
            ],
        };

        if (m || i) {
            return [
                {
                    title: 'Messages',
                    icon: createMessageIcon(unreadCount),
                    path: '/messages',
                },
                baseProfile,
            ];
        }

        if (user?.role === ContractorRole.Admin) {
            return [
                {
                    title: 'Settings',
                    icon: <SettingsIcon />,
                    subItems: [
                        {
                            title: 'Pricing Templates',
                            path: '/pricingTemplates',
                        },
                        {
                            title: 'Notification Settings',
                            path: '/notificationSettings',
                        },
                    ],
                },
                commonMenuItems.help,
                baseProfile,
            ];
        }

        if (user?.role === ContractorRole.User) {
            return [commonMenuItems.help, baseProfile];
        }

        if (String(user?.role ?? '').includes('admin')) {
            return [notificationsSettings, commonMenuItems.help, baseProfile];
        }

        return [notificationsSettings, commonMenuItems.help, baseProfile];
    };

    const secondaryMenuItems = roleStr.includes('owner')
        ? secondaryOwnerMenuItems
        : roleStr === 'admin'
          ? secondaryAdminMenuItems
          : secondaryGuestMenuItems(isMobile, isIpad, loggedInUser);

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

    const { data: guestCompanyInfo } = useGetCompanyGuestsInfoQuery(
        `/guests/${loggedInUser?.company}`,
        { skip: !roleStr.includes('guest') },
    );

    const { data: billingReportData } = useGetBillingReportQuery(
        { guestIds: [loggedInUser?.company] },
        { skip: !roleStr.includes('guest') },
    );

    const hasOverdueInvoices = billingReportData?.some((invoice) => {
        const amountDue = invoice.amountDue;
        const dueDate = new Date(invoice.dueDate);
        const daysOverdue = differenceInCalendarDays(new Date(), dueDate);
        return amountDue > 0 && daysOverdue > 14;
    });

    const editProfile = () => {
        navigate('/profile', {
            state: { [PROFILE_SETUP_HIGHLIGHT_STATE_KEY]: true },
        });
    };

    const dismissProfileSetupToast = () => {
        dismissProfileSetupBanner();
        toast.remove('finishSettingUpProfile');
        toast.remove('validateProfilePicture');
    };

    const billingAccountSummary = () => {
        navigate('/billing?page=1');
    };

    const editBilling = () => {
        navigate('/billing');
    };

    useEffect(() => {
        if (!profileDetails) return;

        if (
            toasts?.find((t: Toast) => t.id === 'faceRecWarning') &&
            !location?.pathname.includes('experiences/reservationRequest')
        ) {
            toast.remove('faceRecWarning');
        }
        if (
            toasts?.find((t: Toast) => t.id === 'conflictError') &&
            !location?.pathname.includes('experiences/reservationRequest')
        ) {
            toast.remove('conflictError');
        }

        const missingProfileSetup = getMandatoryProfileSetupMissingItems(
            profileDetails,
            roleStr,
        );
        const needsProfilePicture = missingProfileSetup.some(
            (item) => item.id === 'profilePicture',
        );
        const needsClearProfilePicture = missingProfileSetup.some(
            (item) => item.id === 'profilePictureInvalid',
        );

        if (missingProfileSetup.length === 0) {
            clearProfileSetupBannerDismissed();
            toast.remove('finishSettingUpProfile');
            toast.remove('validateProfilePicture');
        } else if (!isProfileSetupBannerDismissed()) {
            if (needsProfilePicture) {
                toast.error('Action required!', {
                    closeButton: true,
                    dismissLabel: 'Dismiss',
                    onClose: dismissProfileSetupToast,
                    actionText: 'Finish setting up your profile',
                    actionOnclick: editProfile,
                    actionType: ActionType.NAVIGATE,
                    customId: 'finishSettingUpProfile',
                    duration: Infinity,
                });
                toast.remove('validateProfilePicture');
            } else if (needsClearProfilePicture) {
                toast.error('Action required!', {
                    closeButton: true,
                    dismissLabel: 'Dismiss',
                    onClose: dismissProfileSetupToast,
                    actionText: 'Upload a clear profile picture',
                    actionOnclick: editProfile,
                    actionType: ActionType.NAVIGATE,
                    customId: 'validateProfilePicture',
                    duration: Infinity,
                });
                toast.remove('finishSettingUpProfile');
            }
        } else {
            toast.remove('finishSettingUpProfile');
            toast.remove('validateProfilePicture');
        }

        if (roleStr.includes('guest') && guestCompanyInfo) {
            if (!guestCompanyInfo.billingDetailsAdded) {
                const contact = guestCompanyInfo?.primaryContact;

                toast.error(
                    `Action required! ${
                        !isGuestAdmin &&
                        `Contact your company admin - ${contact} to set up billing details before bookings can be made.`
                    }`,
                    {
                        closeButton: true,
                        ...(isGuestAdmin && {
                            actionText:
                                'Set up your billing details to make bookings.',
                            actionOnclick: editBilling,
                            actionType: ActionType.NAVIGATE,
                        }),
                        customId: 'finishSettingUpBilling',
                        duration: Infinity,
                    },
                );
            } else {
                toast.remove('finishSettingUpBilling');
            }

            if (hasOverdueInvoices && !guestCompanyInfo?.onCreditHold) {
                toast.warning('Action required!', {
                    actionText: isGuestAdmin
                        ? `You have invoices overdue by more than 1 day. Please settle them to continue using 7DAY and avoid being placed on credit hold.`
                        : `You have invoices overdue by more than 1 day. Please contact your company admin - ${guestCompanyInfo?.email} to continue using 7DAY`,
                    actionOnclick: isGuestAdmin
                        ? billingAccountSummary
                        : undefined,
                    actionType: ActionType.NAVIGATE,
                    customId: 'payBills',
                    closeButton: false,
                    duration: 9000,
                });
            } else {
                toast.remove('payBills');
            }

            if (guestCompanyInfo?.onCreditHold) {
                toast.error('Action required!', {
                    closeButton: false,
                    actionText: isGuestAdmin
                        ? `You are on credit hold, please pay outstanding invoices to continue using 7DAY`
                        : `You are on credit hold. Please contact your company admin - ${guestCompanyInfo?.email} to continue using 7DAY`,
                    actionOnclick: isGuestAdmin
                        ? billingAccountSummary
                        : undefined,
                    actionType: ActionType.NAVIGATE,
                    customId: 'creditHoldNotice',
                    duration: Infinity,
                });
            } else {
                toast.remove('creditHoldNotice');
            }
        }
    }, [
        profileDetails,
        location,
        guestCompanyInfo,
        hasOverdueInvoices,
        roleStr,
        isGuestAdmin,
    ]);

    useEffect(() => {
        if (!loggedInUser) {
            [
                'finishSettingUpProfile',
                'finishSettingUpBilling',
                'payBills',
                'creditHoldNotice',
                'faceRecWarning',
            ].forEach((id) => toast.remove(id));
        }
    }, [loggedInUser]);

    const menuRoutes: any = {
        Reservations: roleStr === OwnerRole.Admin ? '' : '/reservations',
        'List View': '/reservations?view=list',
        'Calendar View': '/reservations?view=calendar',
        'Daily Visitors': '/reservations/people',
        'RSVP Requests': '/rsvpRequests',
        'View Profile': '/profile',
        'View Organisation': '/contractorProfile',
        'View Host Profile': `/hosts/${loggedInUser?.company}`,
        Messages: '/messages',
        'Manage Your Listings': `/viewExperiences/all/${getIndividualCompany?.name
            ?.replace(/ /g, '')
            .toLowerCase()}`,
        'Listing Settings': '/adminCenter/host',
        'View All Listings': roleStr.includes('owner')
            ? '/viewExperiences/all'
            : `/viewExperiences/${visitTypeSelected?.view}`,
        Dashboard: '/dashboard',
        'Notification Settings': '/notificationSettings',
        'Pricing Templates': '/pricingTemplates',
        'Job List': '/jobList',
        'Recurring Jobs': '/recurringJobs',
        Users: '/users',
        Clients:
            roleStr === ContractorRole.Admin
                ? '/contractorClients'
                : '/clients',
        Guests: '/guests',
        'Admin Centre': `/adminCentre/${
            roleStr.includes('owner') || roleStr === 'admin' ? 'host' : 'guest'
        }`,
        Contractors: '/contractors',
        Logout: '/login',
        'Top Guests': '/reports/topGuests',
        'Cancellation Report': '/reports/cancellationReport',
        'Top Experiences': '/reports/topExperiences',
        'Top Bookers': '/reports/topBookers',
        'Booker Details': '/reports/bookerDetails',
        'Declined Report': '/reports/declinedReport',
        'Monthly Report': '/reports/monthlyReport',
        'Quarterly Report': '/reports/quarterlyReport',
        'YTD Report': '/reports/ytdReport',
        'FY Report': '/reports/fyReport',
        'Guest Details': '/reports/guestDetails',
        'Reservations Report': '/reports/reservationsReport',
        'Occupancy Report': '/reports/occupancy',
        'Host Payments': '/reports/hostPayments',
        'Guest Invoices': '/reports/guestInvoices',
        'Mailing Lists': '/reports/mailingLists',
        'Help Documentation': 'https://help.7-day.com/hc/en-us',
        'Privacy Policy': '/privacy',
        'Terms of Service': '/terms',
        'Report a problem': '/support',
        Profile: '/profile',
        Experiences: roleStr.includes('owner')
            ? ''
            : `/viewExperiences/${visitTypeSelected?.view}`,
        Reports: '/reports',
        Jobs: roleStr === OwnerRole.Admin ? '' : '/jobs',
        Calendar: '/contractorCalendar',
    };

    const toggleMoreMenu = (item: any) => {
        handleCloseSubMenu();
        navigate(item.path);
    };

    const toggleMenu = (menuTitle: string | number) => {
        setOpenMenus((prevState: any) => ({
            ...prevState,
            [menuTitle]: !prevState[menuTitle],
        }));

        if (
            (menuTitle === 'Reports' && (isMobile || isIpad)) ||
            (menuTitle === 'Reports' &&
                (roleStr === 'admin' || roleStr.includes('owner')))
        ) {
            return;
        }

        const path: any = menuRoutes[menuTitle];

        if (typeof path === 'string' && path.startsWith('http')) {
            window.open(path, '_blank', 'noopener,noreferrer');
            return;
        }

        if (menuTitle === 'Logout') {
            clearAllToasts();
        }

        if (path) {
            if (typeof path === 'string' && path.startsWith('http')) {
                window.open(path, '_blank', 'noopener,noreferrer');
                return;
            }

            // Clicking the Experiences nav should always show the full list,
            // not a stale name-search from a prior browse session.
            if (
                menuTitle === 'Experiences' ||
                (typeof path === 'string' &&
                    path.startsWith('/viewExperiences'))
            ) {
                dispatch(resetExperienceSearchFilter());
            }

            navigate(path);

            const pathChanged = location.pathname !== path;
            if (isMobile || (isIpad && pathChanged)) {
                setIsOpen(!isOpen);
                setMobileOpen(!mobileOpen);
            }
        }
    };

    const handleOpenSubMenu = (title: string, el: HTMLElement) => {
        setOpenMenuKey(title);
        setMenuAnchor(el);
    };

    const handleCloseSubMenu = () => {
        setOpenMenuKey(null);
        setMenuAnchor(null);
    };

    const canMini = Boolean(roleStr.includes('owner')) && !isMobile && !isIpad;
    const [isMini, setIsMini] = useState<boolean>(canMini);

    useEffect(() => {
        if (!canMini) return;
        setIsMini(true);
    }, [canMini]);

    const [railAnchor, setRailAnchor] = useState<HTMLElement | null>(null);
    const [railMenuKey, setRailMenuKey] = useState<string | null>(null);
    const [railMenuItems, setRailMenuItems] = useState<
        { title: string; path: string }[]
    >([]);

    const drawerWidth = canMini && isMini ? COLLAPSED_WIDTH : DRAWER_WIDTH;

    const drawerLogoSrc =
        canMini && isMini
            ? '/illustrations/rocketicon.png'
            : theme.palette.custom.logo;

    const handleRailClose = () => {
        setRailAnchor(null);
        setRailMenuKey(null);
        setRailMenuItems([]);
    };

    const handleItemClick = (item: any, e?: React.MouseEvent<HTMLElement>) => {
        const hasSubs = Boolean(item?.subItems?.length);

        if (canMini && isMini && hasSubs && e?.currentTarget) {
            setRailAnchor(e.currentTarget);
            setRailMenuKey(item.title);
            setRailMenuItems(item.subItems || []);
            return;
        }

        toggleMenu(item.title);
    };

    const closeOwnerDrawer = () => {
        if (isMobile || isIpad) setIsOpen(false);
    };

    const toggleDrawer = () => setIsOpen(!isOpen);

    const isOwnerOrAdmin = roleStr.includes('owner') || roleStr === 'admin';

    useEffect(() => {
        // Expose current owner drawer width for sibling layouts (e.g. Messages)
        // that need to align fixed panels with collapsed/expanded nav states.
        const root = document.documentElement;
        if (isOwnerOrAdmin && !(isMobile || isIpad)) {
            root.style.setProperty('--owner-drawer-width', `${drawerWidth}px`);
        } else {
            root.style.setProperty('--owner-drawer-width', '0px');
        }

        return () => {
            root.style.setProperty('--owner-drawer-width', '0px');
        };
    }, [isOwnerOrAdmin, isMobile, isIpad, drawerWidth]);

    const drawerPaperSx = {
        backgroundColor: 'custom.toolbar',
        color: 'custom.lightTextColor',
        backdropFilter: 'blur(10px)',
    };

    const renderMainList = ({
        items,
        compact,
    }: {
        items: any[];
        compact: boolean;
    }) => {
        return (
            <List sx={{ px: canMini && compact ? 0 : 1 }}>
                {items?.map((item: any) => {
                    const isActive = location.pathname === item?.path;
                    const hasSubs = Boolean(item?.subItems?.length);

                    const row = (
                        <ListItem
                            disablePadding
                            sx={{ px: canMini && compact ? 0 : 1 }}
                        >
                            <ListItemButton
                                aria-label={item.title}
                                onClick={(e) => handleItemClick(item, e)}
                                sx={{
                                    minHeight: 44,
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent:
                                        canMini && compact
                                            ? 'center'
                                            : 'flex-start',
                                    px: canMini && compact ? 1 : 2,
                                    borderRadius: canMini && compact ? 0 : 2,
                                    backgroundColor: 'transparent',
                                    color: isActive
                                        ? 'custom.sidebar.menuItemActiveColor'
                                        : 'inherit',
                                    '&:hover': {
                                        backgroundColor:
                                            'custom.sidebar.menuItemHoverBackgroundColor',
                                    },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: canMini && compact ? 0 : 2,
                                        justifyContent: 'center',
                                        color: isActive
                                            ? 'custom.sidebar.menuItemActiveColor'
                                            : 'inherit',
                                    }}
                                >
                                    {renderMenuItemIcon(
                                        item,
                                        Boolean(canMini && compact),
                                    )}
                                </ListItemIcon>

                                {!canMini || !compact ? (
                                    <>
                                        <ListItemText
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                fontSize: {
                                                    xs: '14px',
                                                    md: '16px',
                                                },
                                                color: 'inherit',
                                            }}
                                            primary={
                                                <MenuLabelWithCount
                                                    label={item.title}
                                                    count={item.badgeCount}
                                                />
                                            }
                                            primaryTypographyProps={{
                                                color: 'inherit',
                                                component: 'div',
                                            }}
                                        />
                                        {hasSubs &&
                                            (openMenus[item.title] ? (
                                                <ExpandLess
                                                    sx={{ color: '#fff' }}
                                                />
                                            ) : (
                                                <ExpandMore
                                                    sx={{ color: '#fff' }}
                                                />
                                            ))}
                                    </>
                                ) : null}
                            </ListItemButton>
                        </ListItem>
                    );

                    return (
                        <Box key={item.title}>
                            {canMini && compact ? (
                                <CustomTooltip
                                    title={item.title}
                                    placement='right'
                                    arrow
                                >
                                    <Box>{row}</Box>
                                </CustomTooltip>
                            ) : (
                                row
                            )}

                            {!canMini || !compact ? (
                                item?.subItems ? (
                                    <Collapse
                                        in={openMenus[item.title]}
                                        timeout='auto'
                                        unmountOnExit
                                    >
                                        <List disablePadding>
                                            {item?.subItems?.map(
                                                (subItem: any) => {
                                                    const isSubActive =
                                                        location.pathname +
                                                            location.search ===
                                                        subItem?.path;

                                                    return (
                                                        <ListItem
                                                            key={subItem?.title}
                                                            sx={{ pl: 4 }}
                                                        >
                                                            <ListItemButton
                                                                onClick={() =>
                                                                    toggleMenu(
                                                                        subItem?.title,
                                                                    )
                                                                }
                                                                sx={{
                                                                    backgroundColor:
                                                                        'transparent',
                                                                    color: isSubActive
                                                                        ? 'custom.sidebar.menuItemActiveColor'
                                                                        : '#fff',
                                                                    '& .MuiTypography-root':
                                                                        {
                                                                            color: isSubActive
                                                                                ? 'custom.sidebar.menuItemActiveColor'
                                                                                : '#fff',
                                                                        },
                                                                    '&:hover': {
                                                                        backgroundColor:
                                                                            'custom.sidebar.menuItemHoverBackgroundColor',
                                                                    },
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    sx={{
                                                                        fontSize:
                                                                            {
                                                                                xs: '14px',
                                                                                md: '16px',
                                                                            },
                                                                        color: '#fff',
                                                                    }}
                                                                    primary={
                                                                        subItem?.title
                                                                    }
                                                                    primaryTypographyProps={{
                                                                        color: '#fff',
                                                                    }}
                                                                />
                                                            </ListItemButton>
                                                        </ListItem>
                                                    );
                                                },
                                            )}
                                        </List>
                                    </Collapse>
                                ) : null
                            ) : null}
                        </Box>
                    );
                })}
            </List>
        );
    };

    const renderDrawerBody = () => {
        const compact = canMini && isMini;

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: isMobile ? '100%' : isIpad ? '82%' : '100%',
                    overflowY: 'hidden',
                    paddingTop: isMobile || isIpad ? '' : '50px',
                    width: 'auto',
                }}
            >
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {renderMainList({ items: mainMenuItems, compact })}
                </Box>

                <Divider />

                <Box sx={{ marginTop: 'auto', overflowY: 'auto' }}>
                    <List sx={{ px: canMini && compact ? 0 : 1 }}>
                        {loggedInUser?.role !== 'admin' && (
                            <NotificationsCenter
                                compact={compact}
                                canMini={canMini}
                                variant='drawer'
                                rowPx={canMini && compact ? 0 : 1}
                            />
                        )}

                        {secondaryMenuItems?.map((item: any) => {
                            const isActive = location.pathname === item?.path;
                            const isProfile =
                                item.title === 'Profile' &&
                                Boolean(item?.subItems?.length);

                            const row = (
                                <ListItem
                                    disablePadding
                                    sx={{ px: canMini && compact ? 0 : 1 }}
                                >
                                    <ListItemButton
                                        onClick={(e) => {
                                            if (!isProfile) {
                                                toggleMenu(item.title);
                                                return;
                                            }

                                            if (drawerProfileOpen) {
                                                closeDrawerProfileMenu();
                                            } else {
                                                openDrawerProfileMenu(
                                                    e.currentTarget,
                                                );
                                            }
                                        }}
                                        sx={{
                                            minHeight: 44,
                                            justifyContent:
                                                canMini && compact
                                                    ? 'center'
                                                    : 'flex-start',
                                            px: canMini && compact ? 1 : 2,
                                            borderRadius:
                                                canMini && compact ? 0 : 2,
                                            color: isActive
                                                ? 'custom.sidebar.menuItemActiveColor'
                                                : 'inherit',
                                            '&:hover': {
                                                backgroundColor:
                                                    'custom.sidebar.menuItemHoverBackgroundColor',
                                            },
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                mr: canMini && compact ? 0 : 2,
                                                justifyContent: 'center',
                                                color: isActive
                                                    ? 'custom.sidebar.menuItemActiveColor'
                                                    : 'custom.sidebar.menuItemColor',
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>

                                        {!canMini || !compact ? (
                                            <ListItemText
                                                primaryTypographyProps={{
                                                    color: 'inherit',
                                                }}
                                                secondaryTypographyProps={{
                                                    color: 'inherit',
                                                }}
                                                primary={
                                                    item.title === 'Profile'
                                                        ? `${profileDetails?.firstName} ${profileDetails?.lastName}`
                                                        : item.title
                                                }
                                                secondary={
                                                    item.title === 'Profile'
                                                        ? loggedInUser?.email
                                                        : null
                                                }
                                            />
                                        ) : null}
                                    </ListItemButton>
                                </ListItem>
                            );

                            return (
                                <Box key={item.title}>
                                    {canMini && compact ? (
                                        <CustomTooltip
                                            title={item.title}
                                            placement='right'
                                            arrow
                                        >
                                            <Box>{row}</Box>
                                        </CustomTooltip>
                                    ) : (
                                        row
                                    )}

                                    {isProfile ? (
                                        <Menu
                                            anchorEl={drawerProfileAnchor}
                                            open={drawerProfileOpen}
                                            onClose={closeDrawerProfileMenu}
                                            anchorOrigin={{
                                                vertical: 'center',
                                                horizontal: 'right',
                                            }}
                                            transformOrigin={{
                                                vertical: 'center',
                                                horizontal: 'left',
                                            }}
                                            sx={{
                                                '& .MuiPaper-root': {
                                                    backgroundColor:
                                                        'custom.toolbar',
                                                    borderRadius: 2,
                                                    minWidth: 220,
                                                },
                                                '& .MuiTypography-root': {
                                                    color: '#fff',
                                                },
                                            }}
                                        >
                                            {item.subItems.map((sub: any) => (
                                                <MenuItem
                                                    key={sub.title}
                                                    onClick={() => {
                                                        closeDrawerProfileMenu();
                                                        toggleMenu(sub.title);
                                                    }}
                                                    sx={{
                                                        color: '#fff',
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'custom.sidebar.menuItemHoverBackgroundColor',
                                                        },
                                                    }}
                                                >
                                                    {sub.title}
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    ) : null}
                                </Box>
                            );
                        })}
                    </List>
                </Box>
            </Box>
        );
    };

    if (isOwnerOrAdmin) {
        const mobileTopTo = logoTo;

        return (
            <Box sx={{ display: isMobile || isIpad ? '' : 'flex' }}>
                {(isMobile || isIpad) && (
                    <MobileTopBar
                        logoSrc={drawerLogoSrc}
                        onMenu={toggleDrawer}
                        to={mobileTopTo}
                    />
                )}

                <Drawer
                    variant={isMobile || isIpad ? 'temporary' : 'permanent'}
                    open={isOpen}
                    onClose={toggleDrawer}
                    PaperProps={{
                        role: 'navigation',
                        'aria-label': 'Main navigation',
                    }}
                    sx={{
                        width: drawerWidth,
                        maxWidth: drawerWidth,
                        flexShrink: 0,
                        transition: 'width 0.2s ease',
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            height: '100%',
                            ...drawerPaperSx,
                            overflow: 'visible',
                            transition: 'width 0.2s ease',
                        },
                    }}
                >
                    {(isMobile || isIpad) && (
                        <DrawerCloseButton onClick={toggleDrawer} />
                    )}

                    <DrawerHeader
                        isMobile={isMobile}
                        isIpad={isIpad}
                        canMini={canMini}
                        isMini={isMini}
                        logoSrc={drawerLogoSrc}
                        onToggleMini={() => {
                            setIsMini((v) => !v);
                            handleRailClose();
                            setOpenMenus({});
                        }}
                        theme={theme}
                        logoTo={logoTo}
                    />

                    {renderDrawerBody()}

                    <Menu
                        anchorEl={railAnchor}
                        open={Boolean(railAnchor) && Boolean(railMenuKey)}
                        onClose={handleRailClose}
                        anchorOrigin={{
                            vertical: 'center',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'center',
                            horizontal: 'left',
                        }}
                        sx={{
                            '& .MuiPaper-root': {
                                backgroundColor: 'custom.toolbar',
                                borderRadius: 2,
                                minWidth: 220,
                            },
                            '& .MuiTypography-root': { color: '#fff' },
                        }}
                    >
                        {railMenuItems.map((sub) => (
                            <MenuItem
                                key={sub.title}
                                onClick={() => {
                                    handleRailClose();
                                    toggleMenu(sub.title);
                                    closeOwnerDrawer();
                                }}
                                sx={{
                                    color: '#fff',
                                    '& .MuiTypography-root': { color: '#fff' },
                                    '&:hover': {
                                        backgroundColor:
                                            'custom.sidebar.menuItemHoverBackgroundColor',
                                    },
                                }}
                            >
                                {sub.title}
                            </MenuItem>
                        ))}
                    </Menu>
                </Drawer>

                {!location?.pathname.includes('ai-assistant') &&
                    roleStr.includes('guest') &&
                    !isMobile && <AIAssistant />}
            </Box>
        );
    }

    return (
        <AppBar
            position='fixed'
            elevation={2}
            sx={{
                width: '100%',
                overflow: 'hidden',
                top: 'var(--app-prompt-banner-height, 0px)',
            }}
        >
            <Toolbar
                disableGutters
                sx={{
                    color: 'primary.contrastText',
                    backgroundColor: 'custom.toolbar',
                    width: 'auto',
                    height: '20px',
                    display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' },
                }}
            >
                <IconButton
                    edge='start'
                    sx={{
                        display: {
                            xs: 'block',
                            sm: 'block',
                            md: 'block',
                            lg: 'none',
                        },
                        marginLeft: '10px',
                    }}
                    onClick={handleDrawerToggle}
                >
                    <MenuIcon />
                </IconButton>

                <Box
                    sx={{
                        height: '40px',
                        display: {
                            xs: 'none',
                            sm: 'none',
                            md: 'none',
                            lg: 'flex',
                        },
                    }}
                >
                    <Link to={logoTo}>
                        <img
                            alt='primarylogo'
                            src={theme.palette.custom.logo}
                            style={{
                                padding: '0px 50px 0px 10px',
                                width: '150px',
                                height: 'auto',
                            }}
                        />
                    </Link>
                </Box>

                <Box
                    sx={{
                        flexGrow: 1,
                        display: {
                            xs: 'none',
                            sm: 'none',
                            md: 'none',
                            lg: 'flex',
                        },
                    }}
                >
                    {mainMenuItems?.map((menu: any) => (
                        <React.Fragment key={menu.title}>
                            <Button
                                onClick={(e: any) => {
                                    if (menu.title.toLowerCase() === 'more') {
                                        handleOpenSubMenu(
                                            menu.title,
                                            e.currentTarget,
                                        );
                                    } else {
                                        toggleMenu(menu.title);
                                    }
                                }}
                                sx={{
                                    my: 2,
                                    color: 'custom.sidebar.menuItemColor',
                                    cursor: 'pointer',
                                    textTransform: 'none',
                                    '&:hover': {
                                        color: 'custom.sidebar.menuItemColor',
                                        backgroundColor:
                                            'custom.sidebar.menuItemHoverBackgroundColor',
                                    },
                                }}
                            >
                                <MenuLabelWithCount
                                    label={menu.title}
                                    count={menu.badgeCount}
                                />
                                {menu.title.toLowerCase() === 'more' &&
                                    menu.icon}
                            </Button>

                            {menu.title.toLowerCase() === 'more' && (
                                <Menu
                                    anchorEl={
                                        openMenuKey === menu.title
                                            ? menuAnchor
                                            : null
                                    }
                                    open={openMenuKey === menu.title}
                                    onClose={handleCloseSubMenu}
                                    sx={{
                                        '& .MuiPaper-root': {
                                            backgroundColor: 'custom.toolbar',
                                            borderRadius: '4px',
                                        },
                                        '& .MuiTypography-root': {
                                            color: 'custom.sidebar.menuItemColor',
                                        },
                                    }}
                                >
                                    {menu?.subItems?.map((item: any) => (
                                        <MenuItem
                                            key={item.title}
                                            onClick={() => toggleMoreMenu(item)}
                                            sx={{
                                                m: 0,
                                                backgroundColor:
                                                    'custom.toolbar',
                                                color: 'custom.sidebar.menuItemColor',
                                                '&:hover': {
                                                    color: 'custom.sidebar.menuItemHoverColor',
                                                    backgroundColor:
                                                        'custom.sidebar.menuItemHoverBackgroundColor',
                                                },
                                            }}
                                        >
                                            {item?.icon && (
                                                <Typography sx={{ pr: 2 }}>
                                                    {item.icon}
                                                </Typography>
                                            )}
                                            <Typography>
                                                {item.title}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Menu>
                            )}
                        </React.Fragment>
                    ))}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                    }}
                >
                    <Stack
                        direction='row'
                        sx={{ alignItems: 'center', gap: 2 }}
                    >
                        {roleStr !== External_Attendee_Role.User && (
                            <IconButton
                                onClick={() => navigate('/messages')}
                                sx={{
                                    p: 0,
                                    color: 'custom.lightTextColor',
                                    '&:hover': {
                                        color: 'custom.profileAvatar',
                                    },
                                }}
                            >
                                <Badge
                                    badgeContent={unreadCount}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            color: 'custom.sidebar.badgeColor',
                                            backgroundColor:
                                                'custom.sidebar.badgeBackgroundColor',
                                        },
                                    }}
                                >
                                    <MailIcon />
                                </Badge>
                            </IconButton>
                        )}

                        {loggedInUser?.role !== 'admin' && (
                            <NotificationsCenter variant='topbar' />
                        )}

                        {secondaryMenuItems?.map((menu: any) => (
                            <Box
                                sx={{
                                    color: 'custom.lightBorderColor',
                                    borderLeft:
                                        menu.title !== 'Profile'
                                            ? 'none'
                                            : '1px solid',
                                    display: 'flex',
                                    justifyContent: isMobile
                                        ? 'center'
                                        : 'flex-end',
                                }}
                                key={menu.title}
                            >
                                <IconButton
                                    disableRipple
                                    onClick={(
                                        e: React.MouseEvent<HTMLElement>,
                                    ) =>
                                        handleOpenSubMenu(
                                            menu.title,
                                            e.currentTarget,
                                        )
                                    }
                                    sx={{
                                        p: 0,
                                        color: 'custom.lightTextColor',
                                        '&:hover': {
                                            cursor: 'pointer',
                                            color: 'custom.profileAvatar',
                                            backgroundColor: 'transparent',
                                            background: 'none',
                                        },
                                    }}
                                >
                                    {menu.title === 'Profile' ? (
                                        <ListItem>
                                            <ListItemAvatar
                                                sx={{ minWidth: '30px', pr: 2 }}
                                            >
                                                {String(
                                                    profileDetails?.role ?? '',
                                                ).includes('company') ? (
                                                    <AccountCircleIcon
                                                        sx={{
                                                            p: 0,
                                                            '&:hover': {
                                                                cursor: 'pointer',
                                                                color: 'custom.sidebar.iconTextColor',
                                                                backgroundColor:
                                                                    'transparent',
                                                                background:
                                                                    'none',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <ProfilePicture
                                                        profileDetails={
                                                            profileDetails
                                                        }
                                                        src={
                                                            profileDetails
                                                                ?.profilePicture
                                                                ?.src
                                                        }
                                                        sx={{ ...avatarStyles }}
                                                    />
                                                )}
                                            </ListItemAvatar>
                                            <ListItemText
                                                primaryTypographyProps={{
                                                    color: 'inherit',
                                                }}
                                                secondaryTypographyProps={{
                                                    color: 'inherit',
                                                }}
                                                primary={
                                                    <>
                                                        {
                                                            profileDetails?.firstName
                                                        }{' '}
                                                        {
                                                            profileDetails?.lastName
                                                        }
                                                    </>
                                                }
                                                secondary={loggedInUser?.email}
                                            />
                                        </ListItem>
                                    ) : (
                                        menu.icon
                                    )}
                                </IconButton>

                                <Menu
                                    anchorEl={
                                        openMenuKey === menu.title
                                            ? menuAnchor
                                            : null
                                    }
                                    open={openMenuKey === menu.title}
                                    onClose={handleCloseSubMenu}
                                    sx={{
                                        '& .MuiPaper-root': {
                                            backgroundColor: 'custom.toolbar',
                                            borderRadius: '4px',
                                        },
                                        '& .MuiTypography-root': {
                                            color: 'custom.lightTextColor',
                                        },
                                    }}
                                >
                                    {menu?.subItems?.map((item: any) => (
                                        <MenuItem
                                            key={item.title}
                                            onClick={() => {
                                                toggleMenu(item.title);
                                                handleCloseSubMenu();
                                            }}
                                            sx={{
                                                m: 0,
                                                backgroundColor:
                                                    'custom.toolbar',
                                                color: 'custom.lightTextColor',
                                                '&:hover': {
                                                    color: 'secondary.main',
                                                    backgroundColor:
                                                        'custom.sidebar.menuItemHoverBackgroundColor',
                                                },
                                            }}
                                        >
                                            <Typography>
                                                {item.title}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </Toolbar>

            <Toolbar
                disableGutters
                sx={{
                    display: { xs: 'flex', sm: 'flex', md: 'flex', lg: 'none' },
                    backgroundColor: 'custom.toolbar',
                    color: 'custom.lightTextColor',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    width: '100vw',
                    overflow: 'hidden',
                    left: 0,
                    right: 0,
                    top: 0,
                    zIndex: 1100,
                }}
            >
                <Box sx={{ flexGrow: 1 }}>
                    <Link to={logoTo}>
                        <img
                            alt='primarylogo'
                            src={theme.palette.custom.logo}
                            style={{
                                padding: '0px 50px 0px 10px',
                                width: '150px',
                                height: 'auto',
                            }}
                        />
                    </Link>
                </Box>

                <Box sx={{ ml: 'auto' }}>
                    <IconButton
                        onClick={handleDrawerToggle}
                        sx={{
                            marginLeft: 'auto',
                            marginRight: 2,
                            color: 'custom.lightTextColor',
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>
            </Toolbar>
            {/* guest mobile */}
            <Drawer
                anchor='left'
                variant={'temporary'}
                open={mobileOpen}
                onClose={handleDrawerToggle}
                sx={{
                    flexShrink: 0,

                    transition: 'width 0.3s ease',
                    '& .MuiDrawer-paper': {
                        backgroundColor: 'custom.toolbar',
                        color: 'custom.lightTextColor',
                        backdropFilter: 'blur(10px)',
                        overflow: 'visible',
                        minWidth: '100%',
                    },

                    position: 'relative',
                }}
            >
                <DrawerCloseButton onClick={handleDrawerToggle} />

                <Toolbar
                    sx={{
                        minHeight: DRAWER_HEADER_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        px: 2,
                    }}
                >
                    <LogoLink to={logoTo} src={drawerLogoSrc} height={42} />
                </Toolbar>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: isMobile ? '100%' : isIpad ? '82%' : '100%',
                        overflowY: 'hidden',
                        paddingTop: isMobile || isIpad ? '' : '50px',
                        width: 'auto',
                    }}
                >
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        <List>
                            {mainMenuItems?.map((item: any) => {
                                const isActive =
                                    location.pathname === item?.path;

                                return (
                                    <div key={item.title}>
                                        <ListItem>
                                            <ListItemButton
                                                onClick={() =>
                                                    toggleMenu(item.title)
                                                }
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    backgroundColor:
                                                        'transparent',
                                                    color: isActive
                                                        ? 'custom.sidebar.menuItemActiveColor'
                                                        : 'inherit',
                                                    '&:hover': {
                                                        backgroundColor:
                                                            'custom.sidebar.menuItemHoverBackgroundColor',
                                                    },
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        color: isActive
                                                            ? 'custom.sidebar.menuItemActiveColor'
                                                            : 'inherit',
                                                    }}
                                                >
                                                    {renderMenuItemIcon(
                                                        item,
                                                        false,
                                                    )}
                                                </ListItemIcon>

                                                <ListItemText
                                                    primaryTypographyProps={{
                                                        color: 'inherit',
                                                        component: 'div',
                                                    }}
                                                    primary={
                                                        <MenuLabelWithCount
                                                            label={item.title}
                                                            count={
                                                                item.badgeCount
                                                            }
                                                        />
                                                    }
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        fontSize: {
                                                            xs: '14px',
                                                            md: '16px',
                                                        },
                                                        color: 'inherit',
                                                    }}
                                                />

                                                {item?.subItems &&
                                                    (openMenus[item.title] ? (
                                                        <ExpandLess
                                                            sx={{
                                                                color: '#fff',
                                                            }}
                                                        />
                                                    ) : (
                                                        <ExpandMore
                                                            sx={{
                                                                color: '#fff',
                                                            }}
                                                        />
                                                    ))}
                                            </ListItemButton>
                                        </ListItem>

                                        {item?.subItems && (
                                            <Collapse
                                                in={openMenus[item.title]}
                                                timeout='auto'
                                                unmountOnExit
                                            >
                                                <List disablePadding>
                                                    {item?.subItems?.map(
                                                        (subItem: any) => {
                                                            const isSubActive =
                                                                location.pathname +
                                                                    location.search ===
                                                                subItem?.path;

                                                            return (
                                                                <ListItem
                                                                    key={
                                                                        subItem?.title
                                                                    }
                                                                    sx={{
                                                                        pl: 4,
                                                                    }}
                                                                >
                                                                    <ListItemButton
                                                                        onClick={() =>
                                                                            toggleMenu(
                                                                                subItem?.title,
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            backgroundColor:
                                                                                'transparent',
                                                                            color: isSubActive
                                                                                ? 'custom.sidebar.menuItemActiveColor'
                                                                                : '#fff',
                                                                            '& .MuiTypography-root':
                                                                                {
                                                                                    color: isSubActive
                                                                                        ? 'custom.sidebar.menuItemActiveColor'
                                                                                        : '#fff',
                                                                                },
                                                                            '&:hover':
                                                                                {
                                                                                    backgroundColor:
                                                                                        'custom.sidebar.menuItemHoverBackgroundColor',
                                                                                },
                                                                        }}
                                                                    >
                                                                        <ListItemText
                                                                            sx={{
                                                                                fontSize:
                                                                                    {
                                                                                        xs: '14px',
                                                                                        md: '16px',
                                                                                    },
                                                                                color: '#fff',
                                                                            }}
                                                                            primary={
                                                                                subItem?.title
                                                                            }
                                                                            primaryTypographyProps={{
                                                                                color: '#fff',
                                                                            }}
                                                                        />
                                                                    </ListItemButton>
                                                                </ListItem>
                                                            );
                                                        },
                                                    )}
                                                </List>
                                            </Collapse>
                                        )}
                                    </div>
                                );
                            })}
                        </List>
                    </Box>

                    <Divider />

                    <Box sx={{ marginTop: 'auto', overflowY: 'auto' }}>
                        <List sx={{ px: 1 }}>
                            {loggedInUser?.role !== 'admin' && (
                                <NotificationsCenter
                                    variant='drawer'
                                    rowPx={1}
                                />
                            )}

                            {secondaryMenuItems?.map((item: any) => {
                                const isActive =
                                    location.pathname === item?.path;
                                const isProfile =
                                    item.title === 'Profile' &&
                                    Boolean(item?.subItems?.length);

                                return (
                                    <Box key={item.title}>
                                        <ListItem disablePadding sx={{ px: 1 }}>
                                            <ListItemButton
                                                onClick={(e) => {
                                                    if (!isProfile) {
                                                        toggleMenu(item.title);
                                                        return;
                                                    }

                                                    if (drawerProfileOpen) {
                                                        closeDrawerProfileMenu();
                                                    } else {
                                                        openDrawerProfileMenu(
                                                            e.currentTarget,
                                                        );
                                                    }
                                                }}
                                                sx={{
                                                    minHeight: 44,
                                                    justifyContent:
                                                        'flex-start',
                                                    px: 2,
                                                    borderRadius: 2,
                                                    backgroundColor:
                                                        'transparent',
                                                    color: isActive
                                                        ? 'custom.sidebar.menuItemActiveColor'
                                                        : 'inherit',
                                                    '&:hover': {
                                                        backgroundColor:
                                                            'custom.sidebar.menuItemHoverBackgroundColor',
                                                    },
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: 0,
                                                        mr: 2,
                                                        justifyContent:
                                                            'center',
                                                        color: isActive
                                                            ? 'custom.sidebar.menuItemActiveColor'
                                                            : 'inherit',
                                                    }}
                                                >
                                                    {item.icon}
                                                </ListItemIcon>

                                                <ListItemText
                                                    sx={{
                                                        fontSize: {
                                                            xs: '14px',
                                                            md: '16px',
                                                        },
                                                        color: 'inherit',
                                                    }}
                                                    primaryTypographyProps={{
                                                        color: 'inherit',
                                                    }}
                                                    secondaryTypographyProps={{
                                                        color: 'inherit',
                                                    }}
                                                    primary={
                                                        item.title ===
                                                        'Profile' ? (
                                                            <>
                                                                {
                                                                    profileDetails?.firstName
                                                                }{' '}
                                                                {
                                                                    profileDetails?.lastName
                                                                }
                                                            </>
                                                        ) : (
                                                            item.title
                                                        )
                                                    }
                                                    secondary={
                                                        item.title === 'Profile'
                                                            ? loggedInUser?.email
                                                            : null
                                                    }
                                                />
                                            </ListItemButton>
                                        </ListItem>

                                        {isProfile ? (
                                            <Menu
                                                anchorEl={drawerProfileAnchor}
                                                open={drawerProfileOpen}
                                                onClose={closeDrawerProfileMenu}
                                                anchorOrigin={{
                                                    vertical: 'center',
                                                    horizontal: 'right',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'center',
                                                    horizontal: 'left',
                                                }}
                                                sx={{
                                                    '& .MuiPaper-root': {
                                                        backgroundColor:
                                                            'custom.toolbar',
                                                        borderRadius: 2,
                                                        minWidth: 220,
                                                    },
                                                    '& .MuiTypography-root': {
                                                        color: '#fff',
                                                    },
                                                }}
                                            >
                                                {item.subItems.map(
                                                    (sub: any) => (
                                                        <MenuItem
                                                            key={sub.title}
                                                            onClick={() => {
                                                                closeDrawerProfileMenu();
                                                                toggleMenu(
                                                                    sub.title,
                                                                );
                                                            }}
                                                            sx={{
                                                                color: '#fff',
                                                                '&:hover': {
                                                                    backgroundColor:
                                                                        'custom.sidebar.menuItemHoverBackgroundColor',
                                                                },
                                                            }}
                                                        >
                                                            {sub.title}
                                                        </MenuItem>
                                                    ),
                                                )}
                                            </Menu>
                                        ) : null}
                                    </Box>
                                );
                            })}
                        </List>
                    </Box>
                </Box>
            </Drawer>

            {!location?.pathname.includes('ai-assistant') &&
                roleStr.includes('guest') &&
                !isMobile && <AIAssistant />}
        </AppBar>
    );
};

export default CustomAppBar;
